import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api from '../services/api';
import { RetentionSample } from '../types';

// Listado de solo lectura de muestras de retención/análisis (pedido de la
// reunión Mica/Lucas + planilla de requerimientos: visible por todos, pero no
// editable desde acá — el alta es automática al completar una orden de
// producción, o manual/restringida desde la pestaña de Inventario > Trazabilidad
// para el caso borde de un lote que necesita la muestra antes de liberarse).
export const RetentionSamplesPage: React.FC = () => {
  const [items, setItems] = useState<RetentionSample[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getRetentionSamples({ from: from || undefined, to: to || undefined });
      setItems(res.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudieron cargar las muestras de retención');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const [y, m, d] = value.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Muestras de retención</Typography>
          <Typography variant="body2" color="text.secondary">
            Listado de solo lectura. Se generan automáticamente al completar una orden de producción
            (o manualmente desde Inventario &gt; Trazabilidad, restringido a Calidad/Dirección Técnica).
          </Typography>
        </Box>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={from} onChange={e => setFrom(e.target.value)} sx={{ minWidth: 160 }} />
            <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={to} onChange={e => setTo(e.target.value)} sx={{ minWidth: 160 }} />
            <Button variant="contained" onClick={load} disabled={loading}>Aplicar filtros</Button>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell>OP origen</TableCell>
                  <TableCell>Lote</TableCell>
                  <TableCell>Vencimiento</TableCell>
                  <TableCell align="right">Retención</TableCell>
                  <TableCell align="right">Análisis</TableCell>
                  <TableCell>Registrado por</TableCell>
                  <TableCell>Notas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(s.createdAt).toLocaleString('es-AR')}</TableCell>
                    <TableCell>{s.productName}</TableCell>
                    <TableCell>{s.orderNumber || (s.productionOrderId ? `#${s.productionOrderId}` : '—')}</TableCell>
                    <TableCell>{s.lotCode || '—'}</TableCell>
                    <TableCell>{formatDate(s.expirationDate)}</TableCell>
                    <TableCell align="right">{s.quantityRetention}</TableCell>
                    <TableCell align="right">{s.quantityAnalysis}</TableCell>
                    <TableCell>{s.createdByName || '—'}</TableCell>
                    <TableCell>{s.notes || '—'}</TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary">Sin muestras registradas.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};
